/**
 * Per-screen attribution table — single source of truth for the
 * AttributionStrip rendered at the top of each action screen by the
 * SceneRouter. Each entry maps a screen ID to `{ actor, notified[] }`.
 *
 * Screens that have no meaningful action (browse-only pages, bridge
 * interstitials, welcome screens) are intentionally absent → the
 * lookup returns null → no strip is rendered.
 *
 * Per-trade actor logic follows the Incoterms in code today:
 *   - T1 / T2: CIF · Aurora (Seller) arranges shipping + insurance
 *   - T3: FOB · Pampagro (Buyer) arranges shipping + insurance
 */
import type { Attribution, Party } from "@/components/scene/AttributionStrip";

// ---------- canonical party records ----------

// T1 / T2 parties
const AURORA: Party     = { label: "Aurora", role: "Seller", flag: "BR" };
const AL_MADAR: Party   = { label: "Al Madar", role: "Buyer", flag: "AE" };
const FAB: Party        = { label: "FAB", role: "Bank", flag: "AE" };
const MAERSK: Party     = { label: "Maersk", role: "Carrier", sublabel: "Reefer" };
const MAPFRE: Party     = { label: "Mapfre Brasil", role: "Insurer", flag: "BR" };
const PINT_AE: Party    = { label: "PINT AE", role: "Rail", flag: "AE" };

// T3 parties
const GULF: Party       = { label: "Gulf Phosphate", role: "Seller", flag: "AE" };
const PAMPAGRO: Party   = { label: "Pampagro", role: "Buyer", flag: "BR" };
const ITAU: Party       = { label: "Itaú", role: "Bank", flag: "BR" };
const HAPAG: Party      = { label: "Hapag-Lloyd", role: "Carrier", sublabel: "Bulk" };
const BRADESCO: Party   = { label: "Bradesco Seguros", role: "Insurer", flag: "BR" };
const UAE_FTA: Party    = { label: "UAE FTA", role: "Rail", flag: "AE" };

// T4 parties (air-cargo · BR → UAE chilled meat)
const VERDE_CARNES: Party = { label: "Verde Carnes", role: "Seller", flag: "BR" };
const AL_BAYT: Party      = { label: "Al Bayt", role: "Buyer", flag: "AE" };
const QATAR_CARGO: Party  = { label: "Qatar Airways Cargo", role: "Carrier", sublabel: "✈ QR8147" };
const PORTO_SEG: Party    = { label: "Porto Seguro", role: "Insurer", flag: "BR" };
const DXB_CARGO: Party    = { label: "DXB Cargo Customs", role: "Authority", flag: "AE" };

// Authorities + rail (shared)
const DUBAI_TRADE: Party = { label: "Dubai Trade", role: "Authority", flag: "AE" };
const MOCCAE: Party      = { label: "MOCCAE", role: "Authority", flag: "AE" };
const ABCC: Party        = { label: "ABCC", role: "Authority", flag: "BR" };
const CDIAL: Party       = { label: "CDIAL Halal", role: "Authority", flag: "BR" };
const MAPA: Party        = { label: "MAPA", role: "Authority", flag: "BR" };
const RECEITA: Party     = { label: "Receita Federal", role: "Authority", flag: "BR" };
const SISCOMEX: Party    = { label: "Siscomex", role: "Authority", flag: "BR" };
const SGS: Party         = { label: "SGS", role: "Authority" };
const FUC: Party         = { label: "FUC", role: "Authority", flag: "AE" };
const GLEIF: Party       = { label: "GLEIF", role: "Authority" };
const ORBITRA_VERIFY: Party = { label: "Orbitra Verification", role: "Platform", sublabel: "Eva · officer" };
const SANCTIONS_RAIL: Party = { label: "Sanctions rail", role: "Platform" };
const ORBITRA: Party     = { label: "Orbitra rail", role: "Platform" };
const PROG_ESCROW: Party = { label: "Programmable Escrow", role: "Rail" };
const POLICY_ENGINE: Party = { label: "Policy engine", role: "Platform" };

// ---------- attribution table ----------

const TABLE: Record<string, Attribution> = {
  // ===== Act 0 — pre-trade discovery =====
  "0.3": { actor: AL_MADAR, notified: [AURORA] },            // outbound interest email
  "0.5": { actor: AURORA,   notified: [AL_MADAR] },          // inbound proposal triage
  "0.6": { actor: AURORA,   notified: [AL_MADAR] },          // proposal accepted

  // ===== Act 1 — buyer onboarding =====
  "1.1": { actor: AL_MADAR, notified: [ORBITRA] },                       // account creation
  "1.2": { actor: AL_MADAR, notified: [ORBITRA, GLEIF] },                // entity registration (LEI lookup)
  "1.3": { actor: SANCTIONS_RAIL, notified: [AL_MADAR, AURORA] },        // authorized signatory + AML screening (auto)
  "1.4": { actor: ORBITRA, notified: [AL_MADAR, ORBITRA_VERIFY] },       // live verification call (Eva attests on behalf of Orbitra)
  "1.5": { actor: ORBITRA, notified: [AL_MADAR, AURORA, GLEIF] },        // OrbID materialisation (Orbitra issues, embeds LEI)
  "1.6": { actor: AL_MADAR, notified: [AURORA] },                        // real-time catalogue + proposal popup
  "1.7": { actor: AL_MADAR, notified: [ORBITRA] },                       // OrbID fee checkout
  "1.8": { actor: ORBITRA, notified: [AL_MADAR, AURORA, GLEIF] },        // OrbID materialisation reveal

  // ===== Act 2 — T1 trace arc =====
  "2.1": { actor: AURORA,   notified: [AL_MADAR, ABCC] },                // contract capture (handshake)
  "2.2": { actor: AURORA,   notified: [AL_MADAR, CDIAL, MAPA, ABCC] },   // document carousel (seller uploads)
  "2.3": { actor: ORBITRA,  notified: [AURORA, AL_MADAR] },              // reconciliation matrix (auto)
  "2.4": { actor: AURORA,   notified: [AL_MADAR, PINT_AE] },             // invoice FSM (seller issues)
  "2.4b": { actor: AL_MADAR, notified: [AURORA, ORBITRA] },              // programmable escrow funded · split handshake
  "2.5": { actor: MAERSK,   notified: [AURORA, AL_MADAR] },              // smart logistics
  "2.6": { actor: ORBITRA,  notified: [AURORA, AL_MADAR, DUBAI_TRADE, MOCCAE] }, // compliance snapshot
  "2.7": { actor: DUBAI_TRADE, notified: [AURORA, AL_MADAR, MOCCAE] },   // single-window reveal

  // ===== Act 3 — T1 finance (Programmable Escrow) =====
  "3.1": { actor: AL_MADAR, notified: [AURORA] },                        // LC composer (escrow)
  "3.2": { actor: AL_MADAR, notified: [AURORA, ORBITRA] },               // funding tile (100% buyer)
  "3.3": { actor: POLICY_ENGINE, notified: [AURORA, AL_MADAR] },         // gate cascade
  "3.4": { actor: PROG_ESCROW, notified: [AURORA, AL_MADAR] },           // escrow issued
  "3.5": { actor: ORBITRA,  notified: [AURORA, AL_MADAR] },              // settlement simulation
  "3.6": { actor: ORBITRA,  notified: [AURORA, AL_MADAR] },              // T1 reveal

  // ===== Act 4 — T2 bank-financed repeat =====
  "4.1": { actor: AL_MADAR, notified: [AURORA] },                        // welcome-back / reorder intent
  "4.2": { actor: AL_MADAR, notified: [AURORA] },                        // reorder draft
  "4.3": { actor: AURORA,   notified: [AL_MADAR] },                      // counter-offer + accept
  "4.4": { actor: AURORA,   notified: [AL_MADAR, FAB] },                 // logistics + insurance picker (CIF · seller)
  "4.5": { actor: AURORA,   notified: [AL_MADAR, FAB] },                 // contract capture
  "4.6": { actor: AURORA,   notified: [AL_MADAR, FAB, CDIAL, MAPA, ABCC] }, // document carousel
  "4.7": { actor: ORBITRA,  notified: [AURORA, AL_MADAR, FAB] },         // reconciliation matrix
  "4.8": { actor: AURORA,   notified: [AL_MADAR, FAB, PINT_AE] },        // invoice FSM
  "4.9": { actor: ORBITRA,  notified: [AURORA, AL_MADAR, FAB, DUBAI_TRADE, MOCCAE] }, // compliance snapshot
  "4.10": { actor: ORBITRA, notified: [AURORA, AL_MADAR, FAB] },         // tariff estimate at LC composition
  "4.11": { actor: AL_MADAR, notified: [AURORA, FAB] },                  // LC composer
  "4.12": { actor: POLICY_ENGINE, notified: [AURORA, AL_MADAR, FAB] },   // gate cascade
  "4.13": { actor: FAB,     notified: [AURORA, AL_MADAR] },              // LC issued
  "4.14": { actor: MAERSK,  notified: [AURORA, AL_MADAR, FAB] },         // smart logistics
  "4.15": { actor: MAPFRE,  notified: [AURORA, AL_MADAR, FAB] },         // insurance cert anchor
  "4.16": { actor: ORBITRA, notified: [AURORA, AL_MADAR, FAB] },         // tariff final + settlement
  "4.17": { actor: ORBITRA, notified: [AURORA, AL_MADAR, FAB] },         // T2 reveal

  // ===== Act 5 — T3 reverse-corridor =====
  "5.1":  { actor: PAMPAGRO, notified: [GULF] },                         // Pampagro RFQ
  "5.2":  { actor: GULF,     notified: [PAMPAGRO] },                     // Gulf Phosphate reply
  "5.3":  { actor: PAMPAGRO, notified: [GULF] },                         // reverse-corridor discovery (seller pick)
  "5.4":  { actor: PAMPAGRO, notified: [GULF, ITAU] },                   // logistics + insurance picker (FOB · buyer)
  "5.5":  { actor: GULF,     notified: [PAMPAGRO, ITAU] },               // contract capture
  "5.6":  { actor: GULF,     notified: [PAMPAGRO, ITAU, SGS, MOCCAE, FUC] }, // document carousel
  "5.7":  { actor: ORBITRA,  notified: [GULF, PAMPAGRO, ITAU] },         // reconciliation matrix
  "5.8":  { actor: GULF,     notified: [PAMPAGRO, ITAU, UAE_FTA] },      // invoice FSM
  "5.9":  { actor: ORBITRA,  notified: [GULF, PAMPAGRO, ITAU, RECEITA, SISCOMEX] }, // compliance snapshot
  "5.10": { actor: ORBITRA,  notified: [GULF, PAMPAGRO, ITAU] },         // tariff estimate at LC composition
  "5.11": { actor: PAMPAGRO, notified: [GULF, ITAU] },                   // LC composer
  "5.12": { actor: POLICY_ENGINE, notified: [GULF, PAMPAGRO, ITAU] },    // gate cascade
  "5.13": { actor: ITAU,     notified: [GULF, PAMPAGRO] },               // LC issued
  "5.14": { actor: HAPAG,    notified: [GULF, PAMPAGRO, ITAU] },         // smart logistics (after LC issuance)
  "5.15": { actor: BRADESCO, notified: [GULF, PAMPAGRO, ITAU] },         // insurance cert anchor (buyer-arranged FOB · post-shipment)
  "5.16": { actor: RECEITA,  notified: [GULF, PAMPAGRO, ITAU] },         // tariff final
  "5.17": { actor: ORBITRA,  notified: [GULF, PAMPAGRO, ITAU] },         // settlement
  "5.18": { actor: ORBITRA,  notified: [GULF, PAMPAGRO, ITAU] },         // T3 reveal

  // ===== Act 7 — T4 air-cargo (Verde Carnes ↔ Al Bayt · Qatar Airways Cargo) =====
  "7.1":  { actor: AL_BAYT,      notified: [VERDE_CARNES] },                                 // Al Bayt RFQ
  "7.2":  { actor: VERDE_CARNES, notified: [AL_BAYT] },                                      // Verde Carnes reply
  "7.3":  { actor: AL_BAYT,      notified: [VERDE_CARNES] },                                 // air-corridor discovery (buyer picks)
  "7.4":  { actor: VERDE_CARNES, notified: [AL_BAYT, FAB, QATAR_CARGO] },                    // logistics+insurance (CIP · seller arranges)
  "7.5":  { actor: VERDE_CARNES, notified: [AL_BAYT, FAB] },                                 // contract capture
  "7.6":  { actor: VERDE_CARNES, notified: [AL_BAYT, FAB, CDIAL, MAPA, ABCC] },              // document carousel (meat-air)
  "7.7":  { actor: ORBITRA,      notified: [VERDE_CARNES, AL_BAYT, FAB] },                   // reconciliation matrix (AWB auto-resolve)
  "7.8":  { actor: VERDE_CARNES, notified: [AL_BAYT, FAB, UAE_FTA] },                        // invoice FSM
  "7.9":  { actor: QATAR_CARGO,  notified: [VERDE_CARNES, AL_BAYT, FAB] },                   // smart logistics (air)
  "7.10": { actor: PORTO_SEG,    notified: [VERDE_CARNES, AL_BAYT, FAB] },                   // insurance cert anchor
  "7.11": { actor: ORBITRA,      notified: [VERDE_CARNES, AL_BAYT, FAB, RECEITA, DXB_CARGO] },// compliance snapshot
  "7.12": { actor: ORBITRA,      notified: [VERDE_CARNES, AL_BAYT, FAB] },                   // tariff estimate at LC composition
  "7.13": { actor: AL_BAYT,      notified: [VERDE_CARNES, FAB] },                            // LC composer (7-day fast-track)
  "7.14": { actor: POLICY_ENGINE, notified: [VERDE_CARNES, AL_BAYT, FAB] },                  // gate cascade
  "7.15": { actor: FAB,          notified: [VERDE_CARNES, AL_BAYT] },                        // LC issued
  "7.16": { actor: DXB_CARGO,    notified: [VERDE_CARNES, AL_BAYT, FAB] },                   // tariff final
  "7.17": { actor: ORBITRA,      notified: [VERDE_CARNES, AL_BAYT, FAB] },                   // settlement
  "7.18": { actor: ORBITRA,      notified: [VERDE_CARNES, AL_BAYT, FAB] },                   // T4 reveal
};

/**
 * Lookup the attribution for a screen. Returns `null` when no entry
 * exists, which signals the SceneRouter to skip rendering the strip
 * (used for browse-only / bridge-interstitial screens like 0.1, 0.2,
 * 3.7, 4.18).
 */
export function getAttribution(screen: string): Attribution | null {
  return TABLE[screen] ?? null;
}
